import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./ResourcesPage.css";
import { CASE_STUDIES, NEWS } from "../data/resourcesData";
import useRevealOnIntersect from "../hooks/useRevealOnIntersect";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";

export default function ResourcesPage() {
  const [tab, setTab] = useState("case-studies"); // "case-studies" | "news"
  const location = useLocation();
  const navigate = useNavigate();

  useScrollToTopOnMount();
  useRevealOnIntersect(".rs-reveal", { threshold: 0.2 }, tab);

  // Sync tab with URL query (?tab=case-studies|news)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tab");
    if (t === "case-studies" || t === "news") setTab(t);
  }, [location.search]);

  const content = tab === "case-studies" ? CASE_STUDIES : NEWS;

  function shareItem(type, item) {
    const url = `${window.location.origin}/resources/${type}/${item.id}`;
    const title = item.title;
    const text = item.excerpt;
    if (navigator.share) {
      navigator.share({ title, text, url }).catch(() => navigator.clipboard?.writeText(url));
    } else {
      navigator.clipboard
        ?.writeText(url)
        .then(() => alert("Link copied to clipboard"))
        .catch(() => window.prompt("Copy this link:", url));
    }
  }

  return (
    <main className="resources-page">
      {/* Hero */}
      <header className="rs-hero">
        <div className="rs-hero-bg" aria-hidden="true">
          <span className="blob b1" />
          <span className="blob b2" />
          <span className="blob b3" />
          <span className="grid-glow" />
        </div>
        <div className="rs-container">
          <h1 className="rs-reveal" data-reveal="up" style={{ "--d": "60ms" }}>
            Resources
          </h1>
          <p className="subtitle rs-reveal" data-reveal="up" style={{ "--d": "140ms" }}>
            Case Studies and News—curated to help you plan, build, and scale with confidence.
          </p>
        </div>
        <div className="rs-hero-accent" aria-hidden="true" />
      </header>

      {/* Tabs (Blogs removed) */}
      <section className="rs-section">
        <div className="rs-container">
          <div className="rs-tabs rs-reveal" data-reveal="up">
            <button
              type="button"
              className={`rs-tab ${tab === "case-studies" ? "active" : ""}`}
              onClick={() => {
                setTab("case-studies");
                navigate("?tab=case-studies", { replace: true });
              }}
            >
              Case Studies
            </button>
            <button
              type="button"
              className={`rs-tab ${tab === "news" ? "active" : ""}`}
              onClick={() => {
                setTab("news");
                navigate("?tab=news", { replace: true });
              }}
            >
              News
            </button>
            <span
              className="rs-tab-underline"
              style={{
                transform: tab === "case-studies" ? "translateX(0%)" : "translateX(100%)",
                width: "calc(50% - 20px)",
              }}
              aria-hidden="true"
            />
          </div>

          {/* Grid */}
          <div className="rs-grid">
            {content.map((item, i) => (
              <article
                key={item.id}
                className="rs-card rs-reveal"
                data-reveal="up"
                style={{ "--d": `${60 + i * 80}ms` }}
              >
                <div className="rs-thumb">
                  {item.img ? (
                    <img
                      src={item.img}
                      alt=""
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : null}
                </div>

                <div className="rs-meta">
                  <span className="rs-date">{item.date}</span>
                  <div className="rs-tags">
                    {item.tags.map((t) => (
                      <span key={t} className="rs-tag">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                <h3 className="rs-title">{item.title}</h3>
                <p className="rs-excerpt">{item.excerpt}</p>

                <div className="rs-actions">
                  <Link
                    className="rs-btn rs-btn-primary"
                    to={`/resources/${tab}/${item.id}`}
                    aria-label={`Open ${tab === "case-studies" ? "case study" : "news"}: ${item.title}`}
                  >
                    <span className="shine" aria-hidden="true" />
                    {tab === "case-studies" ? "Read Case Study" : "Read News"}
                  </Link>

                  <button
                    className="rs-btn rs-btn-secondary"
                    type="button"
                    onClick={() => shareItem(tab, item)}
                    aria-label={`Share link to ${item.title}`}
                  >
                    Share
                  </button>
                </div>

                <span className="card-shine" aria-hidden="true" />
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="rs-section rs-alt">
        <div className="rs-container rs-cta">
          <h2 className="rs-reveal" data-reveal="up">Want updates?</h2>
          <p className="subtitle rs-reveal" data-reveal="up" style={{ "--d": "80ms" }}>
            Subscribe to hear about new case studies and company news.
          </p>
          <form
            className="rs-form rs-reveal"
            data-reveal="up"
            style={{ "--d": "160ms" }}
            onSubmit={(e) => {
              e.preventDefault();
              const email = new FormData(e.currentTarget).get("email");
              if (email) alert(`Subscribed: ${email}`);
              e.currentTarget.reset();
            }}
          >
            <input type="email" name="email" placeholder="Enter your email" required />
            <button type="submit" className="rs-btn rs-btn-primary">
              <span className="shine" aria-hidden="true" />
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}